// What sets one carrier result apart from the next.
//
// FMCSA lists a big carrier under many DOT registrations, often with the same
// legal name and the same HQ address ("J B Hunt Transport Inc" three times
// over). Name and street alone left recruiters guessing, so a result now leads
// with the facts that actually differ: DOT number, fleet size, status.

export interface CarrierOptionCompany {
  companyName?: string | null
  dotNumber?: string | null
  alternativeCarrierName?: string | null
  statusCode?: string | null
  allowedToOperate?: string | null
  powerUnits?: number | null
  address?: {
    street?: string | null
    city?: string | null
    state?: string | null
    country?: string | null
    zipCode?: string | null
  } | null
}

export type CarrierBadgeTone = 'neutral' | 'danger'

export interface CarrierBadge {
  label: string
  tooltip: string
  tone: CarrierBadgeTone
}

const compact = (value?: string | null) =>
  (value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')

/**
 * The DBA, when it tells the recruiter something the legal name does not.
 * "J B Hunt" under "J B Hunt Transport Inc" is noise; "Ad Transport" under
 * "Ts Trucking Llc" is the reason the row matched.
 */
export const distinctDbaName = (company: CarrierOptionCompany) => {
  const dba = company.alternativeCarrierName?.trim()
  if (!dba) return null
  const dbaKey = compact(dba)
  if (!dbaKey || compact(company.companyName).includes(dbaKey)) return null
  return dba
}

export const formatPowerUnits = (powerUnits?: number | null) => {
  if (!powerUnits || powerUnits < 1) return null
  return `${powerUnits.toLocaleString('en-US')} ${
    powerUnits === 1 ? 'truck' : 'trucks'
  }`
}

/** "Lowell, AR", leaving out whatever FMCSA did not return. */
export const formatHeadquarters = (company: CarrierOptionCompany) =>
  [company.address?.city, company.address?.state].filter(Boolean).join(', ') ||
  null

/**
 * FMCSA status worth flagging, most serious first. An active, authorized
 * carrier shows nothing: that is the normal case and a badge on every row is
 * noise. Unknown status (a directory-only result) shows nothing either.
 */
export const carrierBadges = (
  company: CarrierOptionCompany
): CarrierBadge[] => {
  const badges: CarrierBadge[] = []
  if (company.allowedToOperate === 'N') {
    badges.push({
      label: 'Out of service',
      tooltip: 'FMCSA lists this DOT number under an out-of-service order',
      tone: 'danger',
    })
  } else if (company.statusCode && company.statusCode !== 'A') {
    badges.push({
      label: 'Inactive',
      tooltip: 'FMCSA lists this DOT number as inactive',
      tone: 'neutral',
    })
  }
  return badges
}
